<template>
  <Multiselect
    v-bind="data"
    v-model="updateMultiSelect"
    :class="['custom-multiselect', props.validation.$error ? 'invalid' : '']"
    data-cy="custom-multiselect"
    @select="onSelect"
    @deselect="onSelect"
    @clear="onClear"
    @close="onClose"
    @change="onChange"
  >
    <template #option="{ option }">
      <input
        v-if="showCheckBox"
        type="checkbox"
        :checked="isSelected(option)"
      />
      <label :data-cy="`${dataCyId}-${data.label}`">{{
        option[data.label]
      }}</label>
    </template>
  </Multiselect>
  <b-form-invalid-feedback :class="{ 'is-invalid': validation.$error }">
    <VRuntimeTemplate :template="validation?.$errors?.[0]?.$message" />
  </b-form-invalid-feedback>
</template>

<script setup lang="ts">
import VRuntimeTemplate from "vue3-runtime-template";
import Multiselect from "@vueform/multiselect";
import { ref, watch } from "vue";

const props = defineProps({
  options: { type: Array, required: true },
  trackBy: { type: String, required: true },
  label: { type: String, required: false, default: "" },
  placeholder: { type: String, required: false, default: "" },
  modelValue: { type: Array, required: true },
  showCheckBox: { type: Boolean, required: false, default: false },
  validation: { type: Object, default: () => ({}) },
  dataCyId: { type: String, default: "" },
});

const emit = defineEmits([
  "update:modelValue",
  "close:modal",
  "update:select",
  "change:modal",
]);
const updateMultiSelect = ref(props.modelValue);

watch(
  [() => updateMultiSelect.value],
  () => {
    emit("update:modelValue", updateMultiSelect.value);
  },
  { immediate: true },
);

const isSelected = (option) => {
  const isSelect = updateMultiSelect.value.find(
    (x) => x[props.trackBy] === option[props.trackBy],
  );

  return isSelect !== undefined;
};

let data = ref({
  mode: "tags",
  value: updateMultiSelect.value,
  placeholder: props.placeholder,
  closeOnSelect: false,
  searchable: true,
  trackBy: props.trackBy,
  label: props.label,
  hideSelected: false,
  object: true,
  options: props.options,
  valueProp: props.trackBy,
});

const onSelect = (option) => {
  option.selected = !option.selected;
  const foundIndex = updateMultiSelect.value.findIndex(
    (x) => x[props.trackBy] == option[props.trackBy],
  );
  updateMultiSelect.value[foundIndex] = option;
};

const onChange = () => {
  emit("change:modal", updateMultiSelect);
};

const onClose = () => emit("close:modal", updateMultiSelect);

watch(
  [() => props.options],
  () => {
    data.value = {
      ...data.value,
      options: props.options,
    };
  },
  { immediate: true },
);
</script>
<style src="@vueform/multiselect/themes/default.css"></style>

<style lang="scss">
.multiselect,
.multiselect.is-active {
  color: #b7b7b7;
  font-size: 13px;
  background-color: rgba(239, 240, 241, 0.5019607843);
  border: 1px solid #eff0f1;
  min-height: 56px;
  border-radius: 6px;
  box-shadow: none;
  padding: 7px 20px;

  .multiselect-wrapper {
    min-height: auto;
  }

  .multiselect-tags {
    margin: 0;
    padding: 0;
    gap: 5px;
    max-height: 74px;
    overflow: auto;
  }

  .multiselect-tags-search {
    background: transparent;
  }

  .multiselect-tags-search-wrapper {
    margin-bottom: 0;
    margin-left: 0;
  }

  .multiselect-placeholder {
    color: #b7b7b7;
    padding: 0;
    padding-left: 6px;
    width: 100%;
    border-left: 1px solid #b7b7b7;
  }

  .multiselect-tag {
    color: var(--ui-dark, #231f20);
    font-family: Poppins;
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 100%;
    display: flex;
    padding: 10px 13px;
    align-items: center;
    border-radius: 100px;
    border: 1px solid var(--ui-rule, #eff0f1);
    background: var(--ui-white, #fff);
    margin: 0;
  }

  .multiselect-tag-remove {
    padding: 0;
    margin: 0;
    margin-left: 7px;
    color: #b7b7b7;
  }

  .multiselect-caret {
    margin: 0;
    background: #387dff;
    -webkit-mask-image: url(@/assets/img/arrow_down2.svg);
    width: 10px;
    height: 8px;
  }

  .multiselect-dropdown {
    background: #fff;
    border: 0;
    border-left: 1px solid #eff0f1;
    border-right: 1px solid #eff0f1;
    overflow: auto;
    max-height: 112px;
    border-radius: 0;
  }

  .multiselect-option,
  .multiselect-no-results {
    padding: 18px 24px;
    color: #231f20 !important;
    font-size: 13px;
    font-weight: 400;
    border-bottom: 1px solid #eff0f1;
    background-color: #fff !important;
    -webkit-box-shadow: none;
    box-shadow: none;
    width: 100%;
    height: 56px;

    &:hover {
      color: #231f20;
    }
  }

  input[type="checkbox"] {
    position: relative;
    cursor: pointer;
    width: 0;
    height: 100%;

    &:before {
      content: "";
      display: block;
      position: absolute;
      width: 20px;
      height: 20px;
      top: 0;
      left: 0;
      border: 2px solid #bdbdbd;
      border-radius: 100px;
      background-color: white;
    }

    &:checked {
      &::before {
        border-color: #387dff;
      }

      &:after {
        content: "";
        display: block;
        width: 5px;
        height: 10px;
        border: solid #387dff;
        border-width: 0 2px 2px 0;
        -webkit-transform: rotate(45deg);
        -ms-transform: rotate(45deg);
        transform: rotate(45deg);
        position: absolute;
        top: 4px;
        left: 7px;
      }
    }
  }

  label {
    margin: 0;
    padding-left: 32px;
  }
}

.custom-multiselect {
  &.invalid {
    background-color: #fee5e2;
    border-color: #f7553c;
  }
  &:hover,
  &:focus,
  &:active,
  &:active:focus {
    color: #b7b7b7;
    font-size: 13px;
    background-color: #eff0f180;
    border: 1px solid #eff0f1;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
  }

  .invalid-feedback {
    font-size: 11px;
    font-family: Poppins;
    font-style: normal;
    font-weight: 500;
    color: #f7553c;
    line-height: 130%;
    margin-top: 10px;
  }

  .is-invalid {
    display: block;
    border: 1px solid #f7553c;
    background: #fee5e2;

    &::after {
      content: "";
    }
  }
}
</style>
